import CryptoJS from 'crypto-js';

const ENCRYPTION_KEY_STORAGE_KEY = '__amw_secure_storage_key';

// Generate a session-specific encryption key
// The key is persisted in sessionStorage so a reload in the same tab can still
// decrypt; it is discarded when the tab closes
const getEncryptionKey = (() => {
  let cachedKey = null;
  
  return () => {
    if (!cachedKey) {
      try {
        cachedKey = sessionStorage.getItem(ENCRYPTION_KEY_STORAGE_KEY);
      } catch (error) {
        cachedKey = null;
      }
    }

    if (!cachedKey) {
      // Generate a random key based on browser fingerprint and session
      const browserEntropy = navigator.userAgent + navigator.language + screen.colorDepth;
      const sessionEntropy = Date.now().toString() + Math.random().toString();
      cachedKey = CryptoJS.SHA256(browserEntropy + sessionEntropy).toString();
      try {
        sessionStorage.setItem(ENCRYPTION_KEY_STORAGE_KEY, cachedKey);
      } catch (error) {
        // sessionStorage unavailable - key lives for this page only
      }
    }
    return cachedKey;
  };
})();

/**
 * Encrypts and stores data in sessionStorage
 * @param {string} key - Storage key
 * @param {any} value - Value to store (will be JSON stringified)
 */
const setItem = (key, value) => {
  try {
    const jsonValue = JSON.stringify(value);
    const encrypted = CryptoJS.AES.encrypt(jsonValue, getEncryptionKey()).toString();
    sessionStorage.setItem(key, encrypted);
  } catch (error) {
    console.error('SecureStorage: Failed to encrypt and store data', error);    
    sessionStorage.setItem(key, JSON.stringify(value));
  }
};

/**
 * Retrieves and decrypts data from sessionStorage
 * @param {string} key - Storage key
 * @returns {any} Decrypted value or null if not found
 */
const getItem = (key) => {
  try {
    const encrypted = sessionStorage.getItem(key);
    if (!encrypted) return null;
    
    // Try to decrypt
    try {
      const bytes = CryptoJS.AES.decrypt(encrypted, getEncryptionKey());
      const decrypted = bytes.toString(CryptoJS.enc.Utf8);
      
      if (!decrypted) {
        // Decryption failed, might be unencrypted legacy data
        return JSON.parse(encrypted);
      }
      
      return JSON.parse(decrypted);
    } catch (decryptError) {
      // If decryption fails, try parsing as plain JSON (backward compatibility)
      return JSON.parse(encrypted);
    }
  } catch (error) {
    console.error('SecureStorage: Failed to retrieve and decrypt data', error);
    return null;
  }
};

/**
 * Removes an item from sessionStorage
 * @param {string} key - Storage key
 */
const removeItem = (key) => {
  sessionStorage.removeItem(key);
};

/**
 * Clears all sessionStorage
 */
const clear = () => {
  sessionStorage.clear();
  // Keep the key so values written after a clear stay readable across a reload
  try {
    sessionStorage.setItem(ENCRYPTION_KEY_STORAGE_KEY, getEncryptionKey());
  } catch (error) {
    console.error('SecureStorage: Failed to persist encryption key', error);
  }
};

/**
 * Gets the raw encrypted value (for debugging)
 * @param {string} key - Storage key
 * @returns {string|null} Encrypted value
 */
const getRaw = (key) => {
  return sessionStorage.getItem(key);
};

export const secureStorage = {
  setItem,
  getItem,
  removeItem,
  clear,
  getRaw, // For debugging only
};

export default secureStorage;
